import { Component, OnInit, EventEmitter, Input, Output, inject, input } from '@angular/core';
import { PaymentMethodValidatorService } from 'app/services/utilities/payment-method-validator.service';
import { CreditCard } from 'app/models/CreditCard';
import { Sepa } from 'app/models/Sepa';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import PlaceResult = google.maps.places.PlaceResult;
import { Discount, Option } from '@co-assist/library';

@Component({
    selector: 'app-payment-method-form',
    templateUrl: './payment-method-form.component.html',
    styleUrls: ['./payment-method-form.component.scss'],
    standalone: false
})
export class PaymentMethodFormComponent implements OnInit {
    paymentMethodValidator = inject(PaymentMethodValidatorService);
    private formBuilder = inject(UntypedFormBuilder);
    private translate = inject(TranslateService);

    card: CreditCard = new CreditCard();
    sepa: Sepa = new Sepa();
    cardNumberValidity: boolean = false;
    sepaValidity: boolean = false;
    months: Array<string> = [];
    years: Array<string> = [];
    isCreditCardPayment: boolean;
    cardFormGroup: UntypedFormGroup;
    sepaFormGroup: UntypedFormGroup;
    discountService: string;
    discountWatch: string;
    type: string = 'card';
    @Output() paymentDataResult: EventEmitter<any> = new EventEmitter<any>();
    @Output() applyDiscountCode: EventEmitter<Discount> = new EventEmitter<Discount>();
    readonly showDiscountCode = input<boolean>(undefined);
    @Input() showShoppingCart: boolean;
    readonly selectedSubscription = input<any>(undefined);
    readonly selectedOptions = input<Option[]>([]);
    selectedDiscount: Discount;
    dataToSend: { paymentMethod: string, data: any };

    ngOnInit() {
        this.initFormValues();
        this.setCardForm();
    }

    setCardForm() {
        this.cardFormGroup = this.formBuilder.group({
            productID: [null, [Validators.required]],
            number: [null, [Validators.required, this.paymentMethodValidator.cardNumberValidator()]],
            holderName: [null, [Validators.required]],
            expiryMonth: [null, [Validators.required]],
            expiryYear: [null, [Validators.required]],
            cvc: [null, [Validators.required]]
        }, {
            validators: [
                this.paymentMethodValidator.expiryDateValidator(),
                this.paymentMethodValidator.cvcValidator()
            ]
        });
        this.cardFormGroup.get('number').valueChanges.subscribe(value => {
            this.cardFormGroup.get('number').setValue((<string>value).trim(), { emitEvent: false });
            this.cardFormGroup.get('productID').setValue(
                this.paymentMethodValidator.getCorrectProductID(value.replace(/\D/g, ''))
            );
        });
        this.cardFormGroup.valueChanges.subscribe(data => {
            this.card = new CreditCard(data);
            if (data.number) {
                this.card.number = data.number.replace(/\D/g, '');
            }
            this.sendValues('card');
        });
    }

    setSepaForm() {
        this.sepaFormGroup = this.formBuilder.group({
            iban: [null, [Validators.required, this.paymentMethodValidator.ibanValidator()]],
            gender: [null, [Validators.required]],
            firstName: [null, [Validators.required]],
            lastName: [null, [Validators.required]],
            mandateAddress: this.formBuilder.group({
                addressRaw: [null, [Validators.required]],
                city: [null, [Validators.required]],
                countryCode: [null, [Validators.required]],
                houseNumber: [],
                street: [null, [Validators.required]],
                zip: [null, [Validators.required]]
            })
        });
        this.sepaFormGroup.get('iban').valueChanges.subscribe(value => {
            this.sepaFormGroup.get('iban').setValue((<string>value).trim(), { emitEvent: false });
        });
        this.sepaFormGroup.valueChanges.subscribe(data => {
            this.sepa = new Sepa(data);
            if (data.iban) {
                this.sepa.iban = data.iban.replace(/[^A-Z0-9]/g, '');
            }
            this.sendValues('sepa');
        });
    }

    priceCorrection(discount: Discount): void {
        this.selectedDiscount = discount;
        this.applyDiscountCode.emit(discount);
    }

    /**
     *
     * @param field json translation for this field
     */
    getErrorMessage(field: string): string {
        const fieldValue = this.translate.instant(field);
        return this.translate.instant('REGISTRATION.FORM.FIELD_MANDATORY', { fieldName: fieldValue });
    }

    /**
     * Autocompleting function
     */
    onAutocompleteSelected(place: PlaceResult) {
        this.sepaFormGroup.get('mandateAddress').get('addressRaw').patchValue(place.formatted_address);
        const componentForm = {
            street_number: { type: 'short_name', value: 'houseNumber' },
            route: { type: 'long_name', value: 'street' },
            locality: { type: 'long_name', value: 'city' },
            country: { type: 'short_name', value: 'countryCode' },
            postal_code: { type: 'short_name', value: 'zip' },
        };
        for (let i = 0; i < place.address_components.length; i++) {
            const addressType = place.address_components[i].types[0];
            if (componentForm[addressType]) {
                const val = place.address_components[i][componentForm[addressType].type];
                this.sepaFormGroup.get('mandateAddress').get(componentForm[addressType].value).patchValue(val);
            }
        }
        this.sendValues('sepa');
    }

    isValid(formControlName: string): boolean {
        return this.sepaFormGroup && this.sepaFormGroup.get(formControlName).valid;
    }

    /**
     * Initialize date form part
     */
    private initFormValues(): void {
        const date = new Date();
        for (let index = 1; index < 13; index++) {
            this.months.push(index < 10 ? `0${index}` : index.toString());
        }
        for (let index = date.getFullYear(); index <= date.getFullYear() + 6; index++) {
            this.years.push(index.toString());
        }
    }

    private sendValues(type: string): void {
        if (type === 'sepa' && this.sepaFormGroup.valid) {
            this.paymentDataResult.emit(this.sepa);
        } else if (type === 'card' && this.cardFormGroup.valid) {
            this.paymentDataResult.emit(this.card);
        } else {
            this.paymentDataResult.emit(null);
        }
    }


}
